import { inject, ref } from "vue";
import { DrawerConfig } from "../helpers/drawer.js";
import { MAIN_PAGE } from "../consts/injectProvideKeys.js";
import { useImage } from "./useImageUrl.js";
import { getContent } from "@/cms-ui/utils/getContent";
const drawers = ref([]);
export const isDrawerOpen = ref(false);
export function useDrawer() {
  const parentPage = inject(MAIN_PAGE);
  const invokeDrawer = (data, id) => {
    if (!data || id && drawers.value.find((item) => item.id === id)) return;
    const newDrawer = new DrawerConfig(data, id);
    drawers.value.push(newDrawer);
    return newDrawer.result.finally(
      () => drawers.value = drawers.value.filter((drawer) => drawer.id !== newDrawer.id)
    );
  };
  const formatData = (data, page) => {
    if (!data || !page) return;
    const content = [];
    if (hasTextAndImageContent(data) && data.ctype !== "LeadForm") {
      content.push(createTextAndImageBlock(data, page));
    }
    if (data.ctype === "LeadForm" && !data.variants) {
      data.variants = [];
      data.variants.push({ leadForm: data });
    }
    if (data.variants) {
      const variantContent = data.variants?.map((variant) => convertVariantToComponentProps(variant, page));
      content.push(...variantContent.filter(Boolean));
    }
    const isDrawerContent = (item) => item !== void 0;
    return {
      drawerTypeMobile: data.drawerTypeMobile,
      drawerTitle: data.drawerTitle,
      drawerDescription: data.drawerDescription,
      content: content?.filter(isDrawerContent)
    };
  };
  const openDrawer = (drawer, page) => {
    if (!drawer || !page) return;
    const document = getContent(drawer, page, parentPage);
    const documentData = document?.getData();
    const data = formatData(documentData, page);
    invokeDrawer(data, drawer.$ref);
  };
  return {
    drawers,
    invokeDrawer,
    formatData,
    openDrawer
  };
}
function hasTextAndImageContent(data) {
  return Boolean(data.badges || data.heading || data.paragraph || data.imageLink);
}
function createTextAndImageBlock(data, page) {
  const { image } = useImage(data, page);
  return {
    type: "DrawerTextAndImageBlock",
    data: {
      badges: data.badges,
      heading: data.heading,
      paragraph: data.paragraph?.value,
      image
    }
  };
}
function convertAccordionVariant(variant) {
  if (!variant.accordion) return;
  return {
    type: "DrawerAccordion",
    data: {
      topDivider: variant.topDivider,
      items: variant.accordion.map((item) => ({
        title: item.accordionTitle,
        content: item.accordionContent?.value,
        icon: item.icon,
        isOpen: false
      }))
    }
  };
}
function convertTextBlockVariant(variant) {
  if (!variant.textBlock) return;
  return {
    type: "DrawerTextBlock",
    data: {
      topDivider: variant.topDivider,
      title: variant.textBlock.title,
      paragraph: variant.textBlock.paragraph?.value,
      link: variant.textBlock.link
    }
  };
}
function convertButtonVariant(variant) {
  if (!variant.button) return;
  return {
    type: "DrawerButtonBlock",
    data: {
      topDivider: variant.topDivider,
      variant: variant.button.visualVariant,
      label: variant.button.label,
      icon: variant.button.icon,
      action: {
        url: variant.button.action?.url || "",
        openInNewTab: !!variant.button.action?.openInNewTab
      }
    }
  };
}
function convertInfoboxVariant(variant) {
  if (!variant.infobox) return;
  return {
    type: "DrawerInfobox",
    data: {
      topDivider: variant.topDivider,
      variant: variant.infobox.variant.toLowerCase(),
      paragraph: variant.infobox.paragraph.value,
      icon: variant.infobox.icon,
      title: variant.infobox.title
    }
  };
}
function convertDocumentVariant(variant) {
  if (!variant.document) return;
  return {
    type: "DrawerLinkList",
    data: {
      topDivider: variant.topDivider,
      items: variant.document.map((doc) => ({
        docName: doc.name,
        file: doc.file
      }))
    }
  };
}
function convertAssetVariant(variant, page, parentPage) {
  if (!variant.asset) return;
  const { image } = useImage(variant.asset, page, parentPage);
  return {
    type: "DrawerImageBlock",
    data: {
      topDivider: variant.topDivider,
      alt: variant.asset.altText,
      image
    }
  };
}
function convertLeadFormVariant(variant, page, parentPage) {
  if (!variant.leadForm) return;
  const { image: responsiveImage } = useImage(variant.leadForm.imageOrVideo, page, parentPage);
  return {
    type: "LeadForm",
    data: {
      ...variant.leadForm,
      ...variant.leadForm.formSection,
      legalDescription: variant.leadForm?.legalDescription?.value,
      paragraph: variant.leadForm?.paragraph?.value,
      responsiveImage,
      consents: variant.leadForm?.consentsSection.consents,
      consentsValidation: variant.leadForm?.consentsSection.validation,
      consentsTitle: variant.leadForm?.consentsSection.consentsTitle,
      labels: variant.leadForm?.consentsSection.labels
    }
  };
}
function convertHtmlVariant(variant, page, parentPage) {
  if (!variant.html) return;
  const data = getContent(variant.html, page, parentPage)?.getData();
  return {
    type: "DrawerHtmlContainer",
    data
  };
}
function convertVariantToComponentProps(variant, page, parentPage) {
  return convertAccordionVariant(variant) || convertTextBlockVariant(variant) || convertButtonVariant(variant) || convertInfoboxVariant(variant) || convertDocumentVariant(variant) || convertAssetVariant(variant, page, parentPage) || convertLeadFormVariant(variant, page, parentPage) || convertHtmlVariant(variant, page, parentPage);
}
